<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id, real_id_z } from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Stores
	import { updating_devices } from 'src/stores/device-runtime/process';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Alert, Loader, Text } from '@Shelly/shelly-design-system';
	//Components
	import ControlUpdate from '../../../Controls/Single/ControlUpdate.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	//Comparison
	import { compare } from 'compare-versions';

	$: statusStore = getDeviceStatus(id);
	export let device: Device;

	let waitForResponse = false,
		has_update,
		fwVersion,
		newFwVersion: string,
		fwVersionLabel,
		betaVersion: { build_id: string; url: string; version: string },
		testVersion: { build_id: string; url: string; version: string },
		target_fw = 'stable';

	const id = real_id_z(device.id);

	const checkForUpdate = async (force = false) => {
		//waves didn't support check for firmware update commands. Cloud will provide it if any

		waitForResponse = true;

		fwVersion = $statusStore?.['Version:0']?.applicationVersion;
		fwVersionLabel = $format('fw_version', {
			values: {
				version: fwVersion,
				default: 'Your firmware version is: {version}.',
			},
		});

		newFwVersion = $statusStore?.dev?.pending_fw;

		if (
			typeof newFwVersion !== 'undefined' &&
			newFwVersion.length > 0 &&
			compare(newFwVersion, fwVersion, '>')
		) {
			has_update = true;
		}

		waitForResponse = false;
	};

	onMount(() => {
		checkForUpdate();
	});

	let currentFwVersion = '';
	$: if (fwVersionLabel !== undefined) {
		currentFwVersion = fwVersionLabel.split(': ')[1];
	}
</script>

{#if !fwVersion}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{:else if has_update && newFwVersion && !$updating_devices[id]}
	<Text styleType="body-small" class="mb-m ml-s">
		<span class="text-bold d-flex mb-xs"
			>{$format('span_current_firmware_version', {
				default: 'Current Firmware version:',
			})}
		</span>
		<span>{currentFwVersion}</span>
	</Text>
	<Alert
		type="info"
		class="mb-s"
		customIcon="fa-solid fa-arrows-rotate text-primary"
		isHeadingBold
		heading={capFirstChar(
			$format('firmware_available_stable', {
				default: 'available stable version:',
			}),
		)}
		message={newFwVersion}
	/>
	<ControlUpdate
		hasMarginBottomM={(!waitForResponse &&
			testVersion?.build_id &&
			fwVersion != testVersion.build_id) ||
			(!waitForResponse && betaVersion?.build_id && fwVersion != betaVersion.build_id)}
		hasMarginBottomS={true}
		{device}
		{target_fw}
	/>
{:else if !waitForResponse}
	<Alert
		type="success"
		class={classMap({
			'mb-m':
				(!waitForResponse && testVersion?.build_id && fwVersion != testVersion.build_id) ||
				(!waitForResponse && betaVersion?.build_id && fwVersion != betaVersion.build_id),
			'mb-s': 'gen2',
		})}
		isHeadingBold
		heading={$format('device_is_updated', {
			default: 'Your device is up to date:',
		})}
		message={currentFwVersion}
	/>
{/if}
